<script lang="ts">
  /**
   * TimelineTopBar - the Timeline (Dashboard) header on phones: short period
   * title, Today / Search / View options, and a Days · Focus · Tasks switcher
   * with ‹ › period arrows below. Scale, range, display and favorites live in
   * the View options sheet. Contract: plans/mobile-v2.md §3.3, §3.6.
   */
  import { router } from '@inertiajs/svelte';
  import MobileTopBar from './MobileTopBar.svelte';
  import TimelineViewOptionsSheet from './TimelineViewOptionsSheet.svelte';
  import SegmentedControl from './SegmentedControl.svelte';
  import type { DisplayFilter, Range, Scale, View, ViewConfig } from '../../lib/types';
  import { timelineUrlForView, type TimelineState } from '../../lib/timelineUrl';
  import { MOBILE_TIMELINE_VIEWS, getTimelinePeriodLabel } from '../../lib/mobileTimeline';

  interface Props {
    currentMonth: string;
    currentWeek: string;
    currentYear: number;
    scale: Scale;
    view: View;
    range: Range;
    display: DisplayFilter | null;
    viewConfig: ViewConfig;
    onPrevious: () => void;
    onNext: () => void;
    onToday: () => void;
    /** ‹ › in the switcher row. A view with its own period strip can turn them off. */
    showPeriodArrows?: boolean;
  }

  let {
    currentMonth,
    currentWeek,
    currentYear,
    scale,
    view,
    range,
    display,
    viewConfig,
    onPrevious,
    onNext,
    onToday,
    showPeriodArrows = true,
  }: Props = $props();

  let timeline = $derived<TimelineState>({
    scale,
    view,
    range,
    display,
    currentMonth,
    currentWeek,
    currentYear,
    viewConfig,
  });

  let label = $derived(getTimelinePeriodLabel(scale, currentMonth, currentWeek, currentYear));

  let optionsOpen = $state(false);

  function switchView(v: View) {
    const url = timelineUrlForView(timeline, v);
    if (url) router.get(url, {}, { preserveScroll: true });
  }
</script>

<MobileTopBar title={label.title} subtitle={label.subtitle}>
  {#snippet actions()}
    <button type="button" class="ttb-today" onclick={onToday}>Today</button>
    <button
      type="button"
      class="mtb-icon-btn ttb-options"
      aria-label="View options"
      aria-haspopup="dialog"
      aria-expanded={optionsOpen}
      onclick={() => (optionsOpen = true)}
    >
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12" />
        <circle cx="16" cy="6" r="2" />
        <circle cx="10" cy="12" r="2" />
        <circle cx="18" cy="18" r="2" />
      </svg>
    </button>
  {/snippet}
  {#snippet below()}
    <SegmentedControl
      label="Timeline view"
      value={view}
      options={MOBILE_TIMELINE_VIEWS.map((v) => ({
        value: v,
        label: viewConfig.viewLabels[v]?.label ?? v,
        icon: viewConfig.viewLabels[v]?.icon,
      }))}
      onChange={switchView}
    />
    {#if showPeriodArrows}
      <button type="button" class="mtb-icon-btn" aria-label="Previous period" onclick={onPrevious}>
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"
          ><path d="m15 5-7 7 7 7" /></svg
        >
      </button>
      <button type="button" class="mtb-icon-btn" aria-label="Next period" onclick={onNext}>
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m9 5 7 7-7 7" /></svg
        >
      </button>
    {/if}
  {/snippet}
</MobileTopBar>

<TimelineViewOptionsSheet open={optionsOpen} onClose={() => (optionsOpen = false)} {timeline} />

<style>
  .ttb-today {
    position: relative;
    height: 32px;
    margin: 0 4px;
    padding: 0 12px;
    border: 1px solid #e5e7eb;
    border-radius: 999px;
    background: #fff;
    color: #2563eb;
    font: inherit;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  /* 44px hit area around the 32px pill */
  .ttb-today::after {
    content: '';
    position: absolute;
    inset: -6px -2px;
  }

  .ttb-today:active {
    background: #f3f4f6;
  }

  /* Outranks MobileTopBar's `.mtb .mtb-icon-btn` background. */
  button.ttb-options.mtb-icon-btn {
    background: #f3f4f6;
  }

  button.ttb-options.mtb-icon-btn:active {
    background: #e5e7eb;
  }
</style>
